import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { makeEmailUpdateSchema } from "./emailUpdateSchema";
import { useEmailUpdate } from "./useEmailUpdate";

type FormValues = { newEmail: string };

export function EmailUpdateForm() {
  const { user } = useAuth();
  const { updateEmail, isLoading } = useEmailUpdate();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(makeEmailUpdateSchema(user?.email ?? "")),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  const onSubmit = async (values: FormValues) => {
    await updateEmail(values.newEmail);
    reset();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-1">
        <Label>Current Email</Label>
        <Input value={user?.email ?? ""} disabled readOnly />
      </div>

      <div className="space-y-1">
        <Label htmlFor="newEmail">New Email</Label>
        <Input
          id="newEmail"
          type="email"
          placeholder="Enter new email address"
          {...register("newEmail")}
        />
        {errors.newEmail && (
          <p className="text-sm text-destructive">{errors.newEmail.message}</p>
        )}
        <p className="text-sm text-muted-foreground">
          A confirmation email will be sent to the new address before the change takes effect.
        </p>
      </div>

      <Button type="submit" disabled={!isDirty || isLoading}>
        {isLoading ? "Updating…" : "Update Email"}
      </Button>
    </form>
  );
}
